/**
 * Which conversation a risk row is, said on the row.
 *
 * "$255,756 at risk" is three different statements depending on the basis, and
 * they lead to three different meetings:
 *
 *  - Projected overrun -- the burn says it will go over. Somebody can still act.
 *  - Already over      -- it is past budget now. The money is spent.
 *  - Past end date     -- hours booked after the scheduled end.
 *
 * So the basis is a visible cell, never a tooltip. A board that shows only the
 * dollars invites the reader to treat a warning and a post-mortem as the same
 * item, and the top of this board is mostly the second kind.
 */
import { type ChipTone, StatusChip } from "@/components/shared/status-chip";
import type { RiskDollarBasis } from "@/lib/profitability-api";

const BASIS: Record<RiskDollarBasis, { label: string; tone: ChipTone; title: string }> = {
  projected_overrun: {
    label: "Projected overrun",
    tone: "warn",
    title:
      "At the burn rate observed so far, this projects past its budget. A forecast, not a fact -- it assumes the rest of the calendar burns hours at the same average daily rate.",
  },
  realized_overrun: {
    label: "Already over",
    tone: "bad",
    title:
      "The hours booked are already past the budget. This is spend that has happened, not a forecast.",
  },
  hours_past_end: {
    label: "Work past end date",
    tone: "neutral",
    title:
      "Hours booked in periods that start after the demand's end date. Counted by whole period, so a demand ending mid-month is never accused of the month it is still inside.",
  },
};

export function RiskBasisBadge({ basis }: { basis: RiskDollarBasis }) {
  const spec = BASIS[basis];
  // An unknown basis is a service change, not a reason to render nothing: show
  // the raw value so a new kind is visible rather than silently blank.
  if (!spec) {
    return <StatusChip tone="neutral">{basis}</StatusChip>;
  }
  return (
    <StatusChip tone={spec.tone} title={spec.title}>
      {spec.label}
    </StatusChip>
  );
}

/**
 * The second axis, and it is not the same as the basis.
 *
 * `actionable: false` means the scheduled calendar has already ended -- there
 * is nothing left to steer, whatever the basis says. 65 of the 108 ranked rows
 * are in this state today, so hiding them would erase most of the board; they
 * are muted instead, which is what tells a live warning apart from a bill that
 * has already been paid.
 */
export function ActionableMark({ actionable }: { actionable: boolean }) {
  if (actionable) return null;
  return (
    <span
      className="ml-2 whitespace-nowrap text-xs text-muted-foreground"
      title="The scheduled calendar has already ended. This exposure is realised -- it is a record of what was spent, not a warning about what will be."
    >
      settled
    </span>
  );
}

/**
 * How the demand is billed -- the third axis, and the one that decides what an
 * overrun COSTS.
 *
 * On Fixed Price the client pays the agreed number whatever the hours, so an
 * overrun comes straight out of margin and there is no conversation to have
 * about it afterwards. On Time & Material the same hours are billable and the
 * overrun is a scope discussion, uncomfortable but recoverable. Identical
 * dollar figures, opposite meanings -- and the top three rows of this board are
 * Fixed Price while the fourth is T&M, so a reader scanning the money column
 * alone reads the two as the same problem.
 *
 * Colour is NOT used here. The basis badge beside it already carries the row's
 * one colour signal, and a second coloured pill turns every line into an alarm.
 * Weight does the work: fixed-price reads as foreground, T&M recedes.
 */
export function BillingTypeCell({ billingType }: { billingType: string | null }) {
  if (!billingType) {
    return (
      <span
        className="text-xs text-muted-foreground"
        title="No billing type recorded on this demand, so what an overrun costs cannot be said."
      >
        not recorded
      </span>
    );
  }

  // Matches "Fixed Price - Totals" and "Fixed Price - By Period" alike. The
  // two differ in how the fee is scheduled, which does not change the point:
  // the hours are not separately billable either way.
  const kind = billingType.toLowerCase();
  const isFixed = kind.startsWith("fixed price");
  const isNonBillable = kind.startsWith("non-billable");
  const isTimeAndMaterial = kind.startsWith("time & material");

  // There are more billing types in the warehouse than the three this cell has
  // something specific to say about -- "Software Subscription" and "Software
  // Commissions" both appear on the technology drill-in. They fell through to
  // the Time & Material sentence, which told the reader their overrun hours
  // were billable. That is a claim about someone else's contract, so an
  // unrecognised type now says only what is certain: the name, and that the
  // consequence is not known here.
  const title = isFixed
    ? "Fixed Price: the client pays the agreed fee whatever the hours, so every overrun hour comes out of margin."
    : isNonBillable
      ? "Non-Billable: the hours earn nothing at all, so the whole cost is absorbed."
      : isTimeAndMaterial
        ? "Time & Material: overrun hours are billable, so this is a scope conversation rather than a straight margin loss."
        : `${billingType}: not one of the contract types this screen can reason about, so whether extra hours are recoverable is not stated here.`;

  return (
    <span
      className={`whitespace-nowrap text-xs ${
        isFixed || isNonBillable ? "font-medium text-foreground" : "text-muted-foreground"
      }`}
      title={title}
    >
      {billingType}
    </span>
  );
}
